import { type CodecMeshDeviceEventRecord, dayjsTZ } from "@irriga-mais/common";
import { useAtomCallback } from "jotai/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { apiService } from "@/api";
import { WATER_PUMP_TYPE_LABELS } from "@/api/model/water-pump";
import Modal from "@/components/Modal";
import { irrigationPlanByIdAtom, projectByIdAtom, waterPumpsByIdAtom } from "@/store";
import { STATUS_CONFIG } from "../constants";
import type { ReportItem } from "../types";
import { formatDateTime, getStatusConfig } from "../utils";
import { ExecutionDiagnosticsSection } from "./ExecutionDiagnostics";

interface ReportDetailModalProps {
  item: ReportItem | null;
  isOpen: boolean;
  onClose: () => void;
  durationThresholdPercent?: number;
}

async function loadEventsForReportItem(
  licId: string,
  item: ReportItem
): Promise<CodecMeshDeviceEventRecord<{ dateType: string; eventStateType: unknown }>[]> {
  const eventRecords = await apiService.codecMeshDeviceEvent.readItemsByCodecId({
    id: licId,
    start_date: dayjsTZ(item.actual_start_time || item.expected_start_time)
      .subtract(10, "second")
      .toISOString(), // Start a bit earlier to ensure we capture the start event
    end_date: dayjsTZ(item.actual_end_time || item.expected_end_time)
      .add(10, "second")
      .toISOString(),
    irrigation_plan: item.irrigation_plan,
    event_types: ["on"],
  });
  return eventRecords;
}

function formatDurationFromSeconds(totalSeconds: number | null | undefined): string {
  if (totalSeconds == null || !Number.isFinite(totalSeconds) || totalSeconds < 0) {
    return "-";
  }

  const totalMinutes = Math.floor(totalSeconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function getDurationSecondsFromRange(
  startDateTime: string | null | undefined,
  endDateTime: string | null | undefined
): number | null {
  if (!startDateTime || !endDateTime) {
    return null;
  }

  const start = dayjsTZ(startDateTime);
  const end = dayjsTZ(endDateTime);

  if (!start.isValid() || !end.isValid()) {
    return null;
  }

  return Math.max(0, end.diff(start, "second"));
}

/**
 * Modal component for displaying detailed execution report information
 */
export function ReportDetailModal({ item, isOpen, onClose, durationThresholdPercent = 10 }: ReportDetailModalProps) {
  const findItemPlanAndProject = useAtomCallback(
    useCallback(
      (get) => {
        const irrigationPlanById = get(irrigationPlanByIdAtom);
        const projectById = get(projectByIdAtom);
        const waterPumpById = get(waterPumpsByIdAtom);
        const irrigationPlan = item ? irrigationPlanById(item.irrigation_plan) : null;
        const project = irrigationPlan ? projectById(irrigationPlan.project) : null;
        return { irrigationPlan, project, waterPumpById };
      },
      [item?.irrigation_plan, item]
    )
  );
  const [events, setEvents] = useState<CodecMeshDeviceEventRecord<{ dateType: string; eventStateType: unknown }>[]>([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const { irrigationPlan, project, waterPumpById } = findItemPlanAndProject();
  const licId = project?.localized_irrigation_controller;

  useEffect(() => {
    let isActive = true;

    setEvents([]);
    if (!isOpen || !licId || !item) {
      setIsLoadingEvents(false);
      return;
    }

    setIsLoadingEvents(true);
    loadEventsForReportItem(licId, item)
      .then((eventRecords) => {
        if (isActive) {
          setEvents(eventRecords);
        }
      })
      .catch(() => {
        if (isActive) {
          setEvents([]);
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoadingEvents(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [isOpen, licId, item]);
  const equipmentEvents = useMemo(() => {
    return events
      .filter((event) => event.event_type === "on" && event.event_state === true && (event.water_pump || event.sector))
      .map((event) => {
        const waterPump = event.water_pump ? waterPumpById(event.water_pump) : null;
        const equipmentType = event.water_pump
          ? (waterPump ? `Bomba (${WATER_PUMP_TYPE_LABELS[waterPump.pump_type]})` : "Bomba")
          : event.sector ? "Setor" : "Não identificado";
        const startTime = dayjsTZ(event.start_time);
        const endTime = event.end_time ? dayjsTZ(event.end_time) : undefined;
        const step = irrigationPlan?.steps.find((step) => event.sector && step.sector === event.sector);
        const isWaterPumpEvent = Boolean(event.water_pump);
        const realizedDurationInSeconds = isWaterPumpEvent
          ? getDurationSecondsFromRange(item?.actual_start_time, item?.actual_end_time)
          : endTime
            ? Math.max(0, endTime.diff(startTime, "second"))
            : null;
        const scheduledDurationInSeconds = isWaterPumpEvent
          ? getDurationSecondsFromRange(item?.expected_start_time, item?.expected_end_time)
          : step?.duration_seconds;
        const durationDeviationPercent =
          realizedDurationInSeconds != null &&
          scheduledDurationInSeconds != null &&
          Number.isFinite(realizedDurationInSeconds) &&
          Number.isFinite(scheduledDurationInSeconds) &&
          scheduledDurationInSeconds > 0
            ? (Math.abs(realizedDurationInSeconds - scheduledDurationInSeconds) / scheduledDurationInSeconds) * 100
            : null;
        const isDurationOutOfThreshold =
          durationDeviationPercent != null && durationDeviationPercent > durationThresholdPercent;
        const realizedDurationText = formatDurationFromSeconds(realizedDurationInSeconds);
        const scheduledDurationText = formatDurationFromSeconds(scheduledDurationInSeconds);
        const sector = project?.sectors.find((sector) => sector.id === event.sector);
        const name = sector ? sector.name : waterPump ? waterPump.label : event.water_pump ? "Bomba" : "Equipamento Não Identificado";
        return {
          ...event,
          equipmentType,
          startTime,
          endTime,
          realizedDurationText,
          scheduledDurationText,
          isDurationOutOfThreshold,
          step,
          sector,
          name,
        };
      })
      .sort((a, b) => {
        // Keep chronological order across day boundaries while ignoring seconds.
        const aMinuteEpoch = a.startTime.startOf("minute").valueOf();
        const bMinuteEpoch = b.startTime.startOf("minute").valueOf();
        if (aMinuteEpoch !== bMinuteEpoch) {
          return aMinuteEpoch - bMinuteEpoch;
        }

        const aPriority = a.water_pump ? 0 : 1;
        const bPriority = b.water_pump ? 0 : 1;
        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }

        return a.name.localeCompare(b.name, "pt-BR");
      });
  }, [durationThresholdPercent, events, irrigationPlan?.steps, item, project?.sectors, waterPumpById]);
  if (!item) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Detalhes da Execução - ${item.planName}`} size="lg">
      <div className="space-y-4">
        {/* Project name*/}
        <div className="flex justify-center">
          <span className="text-sm font-semibold text-gray-800">Projeto {project?.name}</span>
        </div>
        {/* Status Information */}
        <div className="flex  gap-4">
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-700">Informações de Tempo</h4>
            <div className="space-y-1 text-sm flex-nowrap whitespace-nowrap">
              <div className="flex justify-between">
                <span className="text-gray-600">Início Previsto:</span>
                <span className="font-medium">{formatDateTime(item.expected_start_time)}</span>
              </div>
              <div className="flex justify-between ">
                <span className="text-gray-600">Início Real:</span>
                <span className="font-medium">{formatDateTime(item.actual_start_time)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Fim Previsto:</span>
                <span className="font-medium">{formatDateTime(item.expected_end_time)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Fim Real:</span>
                <span className="font-medium">{formatDateTime(item.actual_end_time)}</span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-700">Execução</h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                {/* if there are problems, show "ERRO" instead of actual status */}
                {item?.status && (
                  <div
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      item.status === "COMPLETED" &&
                      (item.missed_irrigation_steps.length !== 0 || item.missed_fertigation_steps.length !== 0)
                        ? STATUS_CONFIG.PROBLEMAS.color
                        : getStatusConfig(item.status).color
                    }`}
                  >
                    {item.status === "COMPLETED" &&
                    (item.missed_irrigation_steps.length !== 0 || item.missed_fertigation_steps.length !== 0)
                      ? "Parcial"
                      : getStatusConfig(item.status).label}
                  </div>
                )}
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Setores Ativados:</span>
                <span className="font-medium">{item.activated_steps.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Setores Pendentes:</span>
                <span className="font-medium text-red-600">{item.missed_irrigation_steps.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Fertirrigação:</span>
                <span className="font-medium">{item.uses_ferti ? "Sim" : "Não"}</span>
              </div>
            </div>
          </div>
        </div>

        {/** List of equipments with Events with event type "on" and event state "true" */}
        <div className="border-t pt-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Linha do tempo de ativação</h4>
          <table className="w-full text-sm" aria-label="Tabela de execuções do plano de irrigação">
            <thead>
              <tr className="bg-gray-100  ">
                <th rowSpan={2} className="text-left py-1 px-2 font-medium  text-gray-700 align-middle">
                  Equipamento
                </th>
                <th rowSpan={2} className="text-left py-1 px-2 font-medium  text-gray-700 align-middle">
                  Início
                </th>
                <th rowSpan={2} className="text-left py-1 px-2 font-medium  text-gray-700 align-middle">
                  Fim
                </th>
                <th colSpan={2} className="text-center py-1 px-2 font-medium  text-gray-700">
                  Duração
                </th>
              </tr>
              <tr className="bg-gray-50">
                <th className="text-center py-1 px-2 text-xs font-semibold tracking-wide text-gray-500">Realizada</th>
                <th className="text-center py-1 px-2 text-xs font-semibold tracking-wide text-gray-500">Programada</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingEvents ? (
                <tr className="border-b border-gray-300">
                  <td className="py-2 px-2 text-gray-500 text-center" colSpan={5}>
                    Carregando...
                  </td>
                </tr>
              ) : equipmentEvents.length === 0 ? (
                <tr className="border-b border-gray-300">
                  <td className="py-2 px-2 text-gray-500 text-center" colSpan={5}>
                    Sem registros
                  </td>
                </tr>
              ) : (
                equipmentEvents.map((event) => (
                  <tr
                    key={event.start_time + event.mesh_device + event.device_idx}
                    className={`border-b border-gray-300 ${event.isDurationOutOfThreshold ? "bg-amber-50" : ""}`}
                  >
                    <td className="py-1 px-2 text-gray-600">{event.name}</td>
                    <td className="py-1 px-2 text-gray-600">{event.startTime.format("HH:mm")}</td>
                    <td className="py-1 px-2 text-gray-600">{event.endTime?.format("HH:mm") ?? "-"}</td>
                    <td className="py-1 px-2 text-gray-600">{event.realizedDurationText}</td>
                    <td className="py-1 px-2 text-gray-600">{event.scheduledDurationText}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Equipment Status */}
        <div className="border-t pt-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Status dos Equipamentos</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Bomba d'Água:</span>
              <span
                className={`font-medium ${item.uses_waterpump ? (item.waterpump_working ? "text-green-600" : "text-red-600") : "text-gray-400"}`}
              >
                {item.uses_waterpump ? (item.waterpump_working ? "Funcionando" : "Não funcionou") : "Não utilizada"}
              </span>
            </div>
            {item.backwash_start_time && (
              <div className="flex justify-between">
                <span className="text-gray-600">Retrolavagem:</span>
                <span className="font-medium">{formatDateTime(item.backwash_start_time)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Execution diagnosis - only meaningful for executions that did not finish */}
        {item.status === "TIMED_OUT" && <ExecutionDiagnosticsSection diagnostics={item.diagnostics} />}

        {/* Problem Details */}
        <ProblemDetailsSection selectedItem={item} />
      </div>
    </Modal>
  );
}

interface ProblemDetailsSectionProps {
  selectedItem: ReportItem;
}

/**
 * Section component for displaying problem details in the modal
 */
function ProblemDetailsSection({ selectedItem }: ProblemDetailsSectionProps) {
  if (selectedItem.missed_irrigation_steps.length === 0 && selectedItem.missed_fertigation_steps.length === 0) {
    return null;
  }

  return (
    <div className="border-t pt-4">
      <h4 className="text-md font-semibold text-gray-700 mb-2">
        Detalhes {selectedItem.status === "RUNNING" ? "da Execução" : "dos Problemas"}
      </h4>
      <div className="space-y-4 text-sm">
        {/* Irrigation Section */}
        {selectedItem.missed_irrigation_steps.length > 0 && (
          <div className="border-b border-gray-200 pb-2 inset-1">
            <div className="font-bold text-gray-700 mb-1">
              {selectedItem.activated_steps.length} de{" "}
              {selectedItem.activated_steps.length + selectedItem.missed_irrigation_steps.length} setores irrigados
            </div>

            <div className="text-xs text-blue-600">
              Irrigados:{" "}
              {selectedItem.activated_steps.length > 0 &&
              selectedItem.activatedIrrigationSectors &&
              selectedItem.activatedIrrigationSectors.length
                ? selectedItem.activatedIrrigationSectors.join(", ")
                : "Nenhum"}
            </div>

            {selectedItem.missed_irrigation_steps.length > 0 &&
              selectedItem.missedIrrigationSectors &&
              selectedItem.missedIrrigationSectors.length > 0 && (
                <div className="text-xs text-red-600">
                  Não irrigados: {selectedItem.missedIrrigationSectors.join(", ")}
                </div>
              )}
          </div>
        )}

        {/* Fertigation Section - only shown if fertigation is used */}
        {selectedItem.missed_fertigation_steps.length > 0 && (
          <div>
            <div className="font-bold text-gray-700 mb-1">
              {selectedItem.activated_ferti_steps.length} de{" "}
              {selectedItem.activated_ferti_steps.length + selectedItem.missed_fertigation_steps.length} setores
              fertirrigados
            </div>

            <div className="text-xs text-blue-900">
              Fertirrigados:{" "}
              {selectedItem.activated_ferti_steps.length > 0 &&
              selectedItem.activatedFertigationSectors &&
              selectedItem.activatedFertigationSectors.length > 0
                ? selectedItem.activatedFertigationSectors.join(", ")
                : "Nenhum"}
            </div>

            {selectedItem.missed_fertigation_steps.length > 0 &&
              selectedItem.missedFertigationSectors &&
              selectedItem.missedFertigationSectors.length > 0 && (
                <div className="text-xs text-yellow-600">
                  Não fertirrigados: {selectedItem.missedFertigationSectors.join(", ")}
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  );
}
